import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useTenant } from '../../context/TenantContext';

export default function FirstLoginPasswordModal() {
  const { clearMustChangePassword, agencyProfile, currentUser } = useTenant();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const brand = agencyProfile.brandColor || '#0D9488';

  const strength = (() => {
    if (!newPassword) return 0;
    let s = 0;
    if (newPassword.length >= 8) s++;
    if (/[A-Z]/.test(newPassword)) s++;
    if (/[0-9]/.test(newPassword)) s++;
    if (/[^A-Za-z0-9]/.test(newPassword)) s++;
    return s;
  })();

  const strengthLabel = ['', 'Weak', 'Fair', 'Good', 'Strong'][strength];
  const strengthColor = ['', '#EF4444', '#F59E0B', '#10B981', '#0D9488'][strength];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
    if (updateError) { setError(updateError.message); setLoading(false); return; }
    await clearMustChangePassword();
    setLoading(false);
    setDone(true);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-8 pb-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-sm"
              style={{ backgroundColor: brand }}
            >
              {agencyProfile.logo || 'TW'}
            </div>
            <div>
              <p className="font-semibold text-gray-800 text-sm">{agencyProfile.name || 'TreWaz'}</p>
              <p className="text-xs text-gray-400">First time setup</p>
            </div>
          </div>
          {done ? (
            <>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3" style={{ backgroundColor: `${brand}15` }}>
                <i className="ri-shield-check-line text-2xl" style={{ color: brand }} />
              </div>
              <h2 className="text-xl font-bold text-gray-800">You're all set!</h2>
              <p className="text-sm text-gray-400 mt-1">Password updated. Your workspace is ready.</p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-amber-50">
                <i className="ri-key-2-line text-2xl text-amber-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Set your password</h2>
              <p className="text-sm text-gray-400 mt-1">
                Hi <span className="font-medium text-gray-600">{currentUser.name}</span>, you&apos;re using a temporary password. Please set a permanent one to continue.
              </p>
            </>
          )}
        </div>

        <div className="px-8 py-6">
          {done ? (
            <button
              onClick={clearMustChangePassword}
              className="w-full py-3 rounded-xl text-white font-semibold text-sm cursor-pointer transition-opacity hover:opacity-90 whitespace-nowrap"
              style={{ backgroundColor: brand }}
            >
              <i className="ri-arrow-right-line mr-2" />
              Enter My Workspace
            </button>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-400 text-sm" />
                  <p className="text-sm text-red-500">{error}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">New Password</label>
                <div className="relative">
                  <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-teal-400 transition-colors"
                    placeholder="Minimum 8 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowNew((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showNew ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                  </button>
                </div>
                {newPassword && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex-1 flex gap-1">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="h-1 flex-1 rounded-full transition-all duration-300"
                          style={{ backgroundColor: i <= strength ? strengthColor : '#E5E7EB' }}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-medium" style={{ color: strengthColor }}>{strengthLabel}</span>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <i className="ri-lock-password-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm text-gray-700 focus:outline-none focus:border-teal-400 transition-colors"
                    placeholder="Repeat your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    <i className={showConfirm ? 'ri-eye-off-line text-sm' : 'ri-eye-line text-sm'} />
                  </button>
                </div>
                {confirmPassword && confirmPassword !== newPassword && (
                  <p className="text-xs text-red-400 mt-1.5">Passwords do not match</p>
                )}
              </div>

              <div className="bg-gray-50 rounded-xl px-4 py-3">
                <p className="text-xs text-gray-500 font-medium mb-1.5">Password requirements</p>
                <div className="space-y-1">
                  {[
                    { label: 'At least 8 characters', met: newPassword.length >= 8 },
                    { label: 'One uppercase letter', met: /[A-Z]/.test(newPassword) },
                    { label: 'One number', met: /[0-9]/.test(newPassword) },
                    { label: 'One special character', met: /[^A-Za-z0-9]/.test(newPassword) },
                  ].map((req) => (
                    <div key={req.label} className="flex items-center gap-2">
                      <i className={`text-xs ${req.met ? 'ri-checkbox-circle-line text-teal-500' : 'ri-checkbox-blank-circle-line text-gray-300'}`} />
                      <span className={`text-xs ${req.met ? 'text-gray-600' : 'text-gray-400'}`}>{req.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || strength < 2}
                className="w-full py-3 rounded-xl text-white font-semibold text-sm cursor-pointer disabled:opacity-50 transition-opacity hover:opacity-90 flex items-center justify-center gap-2 whitespace-nowrap"
                style={{ backgroundColor: brand }}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin" /> Updating...</>
                ) : (
                  <><i className="ri-shield-check-line" /> Set Password & Enter Workspace</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
